import React from 'react';
import {
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Box,
  Skeleton
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AddEditCarModal, DeleteCarModal } from './CarModals';
import { useCarControllers } from '../CarControllers';

const CarSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    cars,
    loading,
    selectedCar,
    isAddModalOpen,
    isEditModalOpen,
    isDeleteModalOpen,
    handleAddCar,
    handleEditCar,
    handleDeleteCar,
    openAddModal,
    openEditModal,
    openDeleteModal,
    closeModals
  } = useCarControllers();

  const handleCarClick = (carId, event) => {
    // Prevent click if it's coming from the action buttons
    if (event.target.closest('.MuiListItemSecondaryAction-root')) {
      return;
    }
    navigate(`/cars/${carId}`);
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Skeleton width={150} height={32} />
          <Skeleton width={100} height={32} />
        </Box>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height={60} sx={{ my: 1 }} />
        ))}
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">{t('cars.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAddModal}
        >
          {t('cars.addCar')}
        </Button>
      </Box>

      {cars.length === 0 ? (
        <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
          {t('cars.noCars')}
        </Typography>
      ) : (
        <List>
          {cars.map((car) => (
            <ListItem 
              key={car._id} 
              divider
              onClick={(e) => handleCarClick(car._id, e)}
              sx={{ 
                cursor: 'pointer',
                '&:hover': { 
                  backgroundColor: 'rgba(0, 0, 0, 0.04)' 
                }
              }}
            >
              <ListItemText primary={car.name} />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="edit"
                  onClick={() => openEditModal(car)}
                  sx={{ mr: 1 }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => openDeleteModal(car)}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      <AddEditCarModal
        open={isAddModalOpen}
        onClose={closeModals}
        onSubmit={handleAddCar}
        loading={loading}
      />

      <AddEditCarModal
        open={isEditModalOpen}
        onClose={closeModals}
        onSubmit={handleEditCar}
        car={selectedCar}
        loading={loading}
      />

      <DeleteCarModal
        open={isDeleteModalOpen}
        onClose={closeModals}
        onConfirm={() => handleDeleteCar(selectedCar._id)}
        loading={loading}
      />
    </Paper>
  );
};

export default CarSection; 